import { Error, Paging } from "api/baseApiResponseModel/BaseApiResponseModel";
import { CarrierListRequestModel } from "api/merchantRepository/filterFeature/model/carrierList/CarrierListRequestModel";
import { CarrierListResponseModel } from "api/merchantRepository/filterFeature/model/carrierList/CarrierListResponseModel";
import { PackageRegistrationRequestModel } from "api/merchantRepository/packageRegistration/model/PackageRegistrationRequestModel";
import { PackageRegistrationResponseModel } from "api/merchantRepository/packageRegistration/model/PackageRegistrationResponseModel";
import { defaultStoreRepository } from "api/repositories/store/StoreRepository";
import { defaultUserManagementRepository } from "api/repositories/userManagement/UserManagementRepository";
import { BaseViewModel } from "components/base/BaseViewModel";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { strings } from "utils/localizedStrings";
import { ServiceRepo } from "utils/serviceRepo/ServiceRepo";

class AirTimeRegistrationInput implements Disposable {
  public loading = new BehaviorSubject<boolean>(false);
  public detailLoading = new BehaviorSubject<boolean>(false);
  public statusLoading = new BehaviorSubject<Record<string, boolean>>({});
  public fetchList = new BehaviorSubject<PackageRegistrationRequestModel>(
    new PackageRegistrationRequestModel()
  );
  public fetchCarrier = new BehaviorSubject<CarrierListRequestModel>(
    new CarrierListRequestModel()
  );
  public exportExcel = new BehaviorSubject<PackageRegistrationRequestModel>(
    new PackageRegistrationRequestModel()
  );
  public fetchDetail = new BehaviorSubject<PackageRegistrationRequestModel>(
    new PackageRegistrationRequestModel()
  );
  public loadStores = new BehaviorSubject<{ userType: number }>({
    userType: 0,
  });
  public loadStaff = new BehaviorSubject<{
    selectedStore: Array<string>;
    userType: number;
  }>({ selectedStore: [], userType: 0 });

  [Symbol.dispose](): void {
    this.loading.complete();
    this.detailLoading.complete();
    this.fetchList.complete();
    this.exportExcel.complete();
    this.fetchDetail.complete();
    this.loadStores.complete();
    this.loadStaff.complete();
  }
}

class AirTimeRegistrationOuput implements Disposable {
  public savedList = new BehaviorSubject<
    Array<PackageRegistrationResponseModel>
  >([]);
  public savedCarrier = new BehaviorSubject<Array<CarrierListResponseModel>>(
    []
  );
  public savedDetail = new BehaviorSubject<PackageRegistrationResponseModel>(
    {}
  );
  public pagination = new BehaviorSubject<Paging>({});
  public onError = new BehaviorSubject<Error>({});
  public onExportSuccess = new BehaviorSubject<boolean>(false);
  public onCheckStatusSuccess = new BehaviorSubject<boolean>(false);
  public storeOptions = new BehaviorSubject<
    Array<{ label: string; value: string }>
  >([{ label: strings.GlobalLabels.All, value: "" }]);
  public staffOptions = new BehaviorSubject<
    Array<{ label: string; value: string }>
  >([{ label: strings.GlobalLabels.All, value: "" }]);

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedDetail.complete();
    this.pagination.complete();
    this.onError.complete();
    this.onExportSuccess.complete();
    this.onCheckStatusSuccess.complete();
    this.storeOptions.complete();
    this.staffOptions.complete();
  }
}

@injectable()
export class AirTimeRegistrationViewModel extends BaseViewModel<
  AirTimeRegistrationInput,
  AirTimeRegistrationOuput
> {
  constructor() {
    super(new AirTimeRegistrationInput(), new AirTimeRegistrationOuput());
  }
  private repo = ServiceRepo.getAirTimeRegistrationRepo();
  private filterRepo = ServiceRepo.getFilterFeatureRepo();

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.repo.getList(params);
        if (res?.data) {
          this.output.savedList.next(res?.data ?? []);
          this.output.pagination.next(res?.pagination);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
        this.output.savedList.next([]);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchCarrier, async (params) => {
      try {
        const res = await this.filterRepo.getCarrierList(params);
        if (res?.data) {
          this.output.savedCarrier.next(res?.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.input.detailLoading.next(true);
        const res = await this.repo.getDetail(params);
        if (res?.data) {
          this.output.savedDetail.next(res?.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.detailLoading.next(false);
      }
    });

    this.skipFirstInitial(this.input.exportExcel, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.repo.exportReport(params);
        if (res?.data) {
          const url = window.URL.createObjectURL(res?.data as Blob);
          const link = document.createElement("a");
          const timestamp = dayjs().format("YYYYMMDD_HHmmss");
          link.href = url;
          link.setAttribute("download", `AIR_TIME_REPORT_${timestamp}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          this.output.onExportSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.loadStores, async ({ userType }) => {
      try {
        const cache = productCache.getCache("storeList");
        if (userType === 3) {
          if (isEmpty(cache)) {
            const res = await defaultStoreRepository.getStoreList({
              page: 0,
              limit: 100,
              status: 0
            });
            const items = res?.data?.data?.items || [];
            const opts = [
              { label: strings.GlobalLabels.All, value: "" },
              ...items.map((s) => ({
                label: s?.name || s?.code,
                value: s?.userId,
                code: s?.code,
              })),
            ];
            productCache.saveCache(
              opts as unknown as Record<string, unknown>,
              "storeList"
            );
            this.output.storeOptions.next(opts);
          } else {
            this.output.storeOptions.next(cache);
          }
        }
      } catch (e) {
        this.output.storeOptions.next([
          { label: strings.GlobalLabels.All, value: "" },
        ]);
      }
    });

    this.skipFirstInitial(
      this.input.loadStaff,
      async ({ selectedStore, userType }) => {
        let isAgency = userType === 3 && !isEmpty(selectedStore);
        let isStore = userType === 4;
        if (isEmpty(selectedStore) && userType === 3) {
          this.output.staffOptions.next([
            {
              label: strings.GlobalMessage.InvalidStore,
              value: "",
            },
          ]);
        } else {
          try {
            if (isAgency || isStore) {
              const res = await defaultUserManagementRepository.getUserListV2({
                page: 0,
                limit: 500,
                status: 0,
                ...(selectedStore ? { store: selectedStore } : {}),
              });
              const items = res?.data?.items || [];
              const opts = [
                { label: strings.GlobalLabels.All, value: "" },
                ...items.map((u) => ({
                  label: u?.fullName || u?.userName || u?.phone,
                  value: u?.id,
                  type: u?.type,
                })),
              ];
              this.output.staffOptions.next(opts);
            }
          } catch (e) {
            this.output.staffOptions.next([
              { label: strings.GlobalLabels.All, value: "" },
            ]);
          }
        }
      }
    );
  }
}
